
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Users, TrendingUp, Clock, CheckCircle } from "lucide-react";
import { useClientAnalytics } from "@/hooks/useClientAnalytics";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";

const statusColors: Record<string, string> = {
  'confirmed': '#10b981',
  'waiting': '#f59e0b',
  'in_progress': '#3b82f6',
  'pricing_requested': '#8b5cf6',
  'pricing_sent': '#06b6d4',
  'booking_confirmed': '#059669',
  'traveling': '#dc2626',
  'customer_service': '#ea580c',
  'cancelled': '#6b7280'
};

const statusLabels: Record<string, string> = {
  'confirmed': 'مؤكد',
  'waiting': 'في الانتظار',
  'in_progress': 'قيد التنفيذ',
  'pricing_requested': 'طلب تسعير',
  'pricing_sent': 'تم إرسال السعر',
  'booking_confirmed': 'تأكيد الحجز',
  'traveling': 'في رحلة',
  'customer_service': 'خدمة العملاء',
  'cancelled': 'ملغي'
};

const ClientStatusAnalysis = () => {
  const { data, isLoading } = useClientAnalytics();

  if (isLoading) {
    return (
      <Card className="bg-white/80 backdrop-blur-sm border-slate-200/60">
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            <div className="h-48 bg-gray-200 rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const chartData = Object.entries(data?.statusAnalysis || {}).map(([status, count]) => ({
    name: statusLabels[status] || status,
    value: count as number,
    color: statusColors[status] || '#6b7280'
  }));

  return (
    <Card className="bg-white/80 backdrop-blur-sm border-slate-200/60">
      <CardHeader>
        <CardTitle className="text-xl font-bold text-slate-800 flex items-center gap-2">
          <Users className="w-5 h-5 text-blue-600" />
          تحليل حالة العملاء
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* رسم بياني دائري */}
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* إحصائيات تفصيلية */}
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-blue-600" />
                <span className="font-medium text-slate-700">إجمالي العملاء</span>
              </div>
              <Badge variant="secondary" className="text-lg font-bold">
                {data?.totalClients || 0}
              </Badge>
            </div>

            {Object.entries(data?.statusAnalysis || {}).map(([status, count]) => (
              <div key={status} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
                <div className="flex items-center gap-2">
                  <div 
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: statusColors[status] || '#6b7280' }}
                  ></div>
                  <span className="text-sm text-slate-600">
                    {statusLabels[status] || status}
                  </span>
                </div>
                <Badge variant="outline">{count as number}</Badge>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ClientStatusAnalysis;
